/* One visible panel, one scroll container, one reversible page lock.
 * A native modal puts the navigation above third-party chat without mutating it.
 */
(() => {
  class HeaderDrawer extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.details = this.querySelector('details');
      this.summary = this.querySelector('summary');
      this.drawer = this.querySelector('dialog');
      if (!this.details || !this.drawer) return;
      this.panels = [...this.querySelectorAll('[data-nav-panel]')];
      this.backButton = this.querySelector('[data-nav-back]');
      this.closeButton = this.querySelector('[data-nav-close]');
      this.headingNode = this.querySelector('[data-nav-title]');
      this.search = this.querySelector('[data-designer-search]');
      this.clearButton = this.querySelector('[data-designer-clear]');
      this.rows = [...this.querySelectorAll('[data-designer-item]')];
      this.isOpen = false;
      this.stack = [];
      this.summary.addEventListener('click', event => {
        event.preventDefault();
        this.toggleDrawer(event);
      }, { signal });
      // A pointer-triggered focus handoff must not turn the browser's quiet
      // source focus into a decorative ring on the destination (WebKit 247416).
      // Keyboard use immediately returns every control, including the closed
      // drawer's opener, to the browser's native :focus-visible indication.
      document.addEventListener('keydown', () => {
        this.pendingNavigation = null;
        this.clearPointerFocus();
      }, { capture: true, signal });
      // Re-evaluate native indication after returning from browser/settings UI.
      window.addEventListener('focus', () => {
        this.pendingNavigation = null;
        this.clearPointerFocus();
      }, { signal });
      this.addEventListener('focusout', event => {
        event.target.removeAttribute('data-nav-pointer-focus');
        if (this.pendingNavigation?.link === event.target) this.pendingNavigation = null;
      }, { signal });
      this.drawer.addEventListener('click', event => this.onClick(event), { signal });
      this.drawer.addEventListener('cancel', event => {
        event.preventDefault();
        this.close();
      }, { signal });
      this.drawer.addEventListener('close', () => {
        // Ignore a queued close event if the shopper already reopened it.
        if (!this.drawer.open && this.isOpen) this.close();
      }, { signal });
      this.drawer.addEventListener('keydown', event => this.onKeydown(event), { signal });
      this.search?.addEventListener('input', () => this.filterDesigners(), { signal });
      this.clearButton?.addEventListener('click', () => {
        this.search.value = '';
        this.filterDesigners();
        this.search.focus({ preventScroll: true });
      }, { signal });
      const reset = () => {
        this.close({ restoreFocus: false });
        this.clearPointerFocus();
      };
      window.addEventListener('pagehide', () => {
        this.suspendedOpen = this.isOpen;
        const active = document.activeElement;
        this.resumePointerFocus = this.pendingNavigation?.link === active
          ? this.pendingNavigation.pointerFocus
          : !!active?.hasAttribute('data-nav-pointer-focus');
        this.pendingNavigation = null;
        // Commit, not click: preserve the origin scroll position for Back.
        // Native dialog.close() restores its saved opener even when our
        // restoreFocus flag is false. Do not close/focus the departing document.
        this.unlockPage();
      }, { signal });
      window.addEventListener('pageshow', () => {
        if (this.suspendedOpen) {
          this.suspendedOpen = false;
          // Returning from the back-forward cache is a return to the origin,
          // where closing the restored menu and focusing its opener is useful.
          const active = document.activeElement;
          // A browser focus preference may have changed while away. Reuse
          // quiet provenance only if the actual source still requests none.
          const stillQuiet = this.drawer.contains(active) &&
            active.matches('button, a[href], summary') && !active.matches(':focus-visible');
          this.close({ pointerFocus: this.resumePointerFocus && stillQuiet });
          this.resumePointerFocus = false;
        }
      }, { signal });
      this.breakpoint = window.matchMedia('(min-width: 990px)');
      this.breakpoint.addEventListener('change', () => {
        if (this.classList.contains('mobile-drawer') && this.breakpoint.matches) reset();
      }, { signal });
      window.addEventListener('resize', () => this.updateViewport(), { signal });
      window.visualViewport?.addEventListener('resize', () => this.updateViewport(), { signal });
      window.visualViewport?.addEventListener('scroll', () => this.updateViewport(), { signal });
      this.showPanel(this.panels[0]);
      this.setupDesktop(signal);
    }

    disconnectedCallback() {
      this.close({ restoreFocus: false });
      this.clearPointerFocus();
      this.controller?.abort();
      this.desktopObserver?.disconnect();
      clearTimeout(this.desktopCloseTimer);
    }

    toggleDrawer(event) { this.isOpen ? this.close() : this.open(event); }
    instantlyHideDrawer() { this.close({ restoreFocus: false }); }

    clearPointerFocus() {
      this.querySelectorAll('[data-nav-pointer-focus]').forEach(control => {
        control.removeAttribute('data-nav-pointer-focus');
      });
    }

    pointerFocusFrom(event, source) {
      // Keyboard/assistive activation, text entry, failed focus and unknown
      // sources keep native focus indication. Never infer input from width.
      if (!(event?.detail > 0) || !source?.matches('button, a[href], summary')) return false;
      const inheritedPointerFocus = source.hasAttribute('data-nav-pointer-focus');
      // Safari does not normally focus buttons on tap. Focus the real control
      // before sampling the UA heuristic, including "always show focus" prefs.
      source.focus({ preventScroll: true });
      return document.activeElement === source &&
        (inheritedPointerFocus || !source.matches(':focus-visible'));
    }

    focusControl(control, pointerFocus = false) {
      control.toggleAttribute('data-nav-pointer-focus', pointerFocus);
      control.focus({ preventScroll: true });
    }

    open(event) {
      if (this.isOpen || !this.isConnected) return;
      const pointerFocus = this.pointerFocusFrom(event, this.summary);
      // Restore the actual opener even on browsers that do not focus on tap.
      this.returnFocus = this.summary;
      this.stack = [];
      if (this.search) {
        this.search.value = '';
        this.filterDesigners();
      }
      this.showPanel(this.panels[0]);
      this.lockPage();
      this.isOpen = true;
      this.details.open = true;
      this.summary.setAttribute('aria-expanded', 'true');
      this.updateViewport();
      this.closeButton.toggleAttribute('data-nav-pointer-focus', pointerFocus);
      // Close has native autofocus: no intermediate logo -> Close focus move,
      // and no search autofocus that would launch the phone keyboard.
      this.drawer.showModal();
    }

    close({ restoreFocus = true, pointerFocus = false } = {}) {
      if (!this.details) return;
      const wasOpen = this.isOpen;
      const target = this.returnFocus?.isConnected ? this.returnFocus : this.summary;
      this.pendingNavigation = null;
      // Set provenance before the browser's synchronous native restoration,
      // not after focus has reached the shared header.
      if (wasOpen) {
        const active = document.activeElement;
        // CountryDrawer also calls close({ restoreFocus: false }) before
        // opening. Preserve its focused control's native indication while
        // native close briefly hands back to the header; never reset inputs.
        const quietHandoff = !restoreFocus && this.drawer.contains(active) &&
          active.matches('button, a[href], summary') &&
          (active.hasAttribute('data-nav-pointer-focus') || !active.matches(':focus-visible'));
        target?.toggleAttribute('data-nav-pointer-focus', pointerFocus || quietHandoff);
      }
      this.isOpen = false;
      if (this.drawer.open) this.drawer.close();
      this.details.open = false;
      this.summary.setAttribute('aria-expanded', 'false');
      this.unlockPage();
      if (wasOpen && restoreFocus) {
        if (target?.getClientRects().length) this.focusControl(target, pointerFocus);
      }
    }

    lockPage() {
      if (this.pageLock) return;
      const bodyStyle = document.body.style;
      const properties = ['position', 'top', 'left', 'right', 'width', 'overflow', 'padding-right'];
      this.pageLock = {
        x: window.scrollX, y: window.scrollY,
        styles: properties.map(name => [name, bodyStyle.getPropertyValue(name), bodyStyle.getPropertyPriority(name)])
      };
      const gutter = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      if (gutter) bodyStyle.setProperty('padding-right', `${parseFloat(getComputedStyle(document.body).paddingRight) + gutter}px`);
      bodyStyle.setProperty('position', 'fixed');
      bodyStyle.setProperty('top', `${-this.pageLock.y}px`);
      bodyStyle.setProperty('left', `${-this.pageLock.x}px`);
      bodyStyle.setProperty('right', '0');
      bodyStyle.setProperty('width', '100%');
      bodyStyle.setProperty('overflow', 'hidden');
    }

    unlockPage() {
      if (!this.pageLock) return;
      const lock = this.pageLock;
      this.pageLock = null;
      lock.styles.forEach(([name, value, priority]) => {
        if (value) document.body.style.setProperty(name, value, priority);
        else document.body.style.removeProperty(name);
      });
      // The theme may enable smooth scrolling. Restoration must be immediate.
      const rootStyle = document.documentElement.style;
      const behavior = [rootStyle.getPropertyValue('scroll-behavior'), rootStyle.getPropertyPriority('scroll-behavior')];
      rootStyle.setProperty('scroll-behavior', 'auto', 'important');
      window.scrollTo(lock.x, lock.y);
      if (behavior[0]) rootStyle.setProperty('scroll-behavior', ...behavior);
      else rootStyle.removeProperty('scroll-behavior');
    }

    updateViewport() {
      if (!this.isOpen) return;
      const viewport = window.visualViewport;
      // Covers only the visible viewport: no duplicated site-header offsets.
      this.drawer.style.setProperty('--dn-visible-height', `${viewport?.height || window.innerHeight}px`);
      this.drawer.style.setProperty('--dn-visible-top', `${viewport?.offsetTop || 0}px`);
    }

    showPanel(panel) {
      if (!panel) return;
      this.panels.forEach(item => {
        item.hidden = item !== panel;
        item.inert = item !== panel;
      });
      this.activePanel = panel;
      panel.querySelector('[data-nav-scroll]').scrollTop = 0;
      this.headingNode.textContent = panel.dataset.navHeading;
      this.backButton.hidden = panel === this.panels[0];
      this.querySelectorAll('[data-nav-target]').forEach(trigger => {
        trigger.setAttribute('aria-expanded', String(trigger.dataset.navTarget === panel.id));
      });
    }

    onClick(event) {
      if (event.target.closest('[data-nav-close]')) {
        return this.close({ pointerFocus: this.pointerFocusFrom(event, this.closeButton) });
      }
      if (event.target.closest('[data-nav-back]')) {
        const previous = this.stack.pop();
        if (!previous) return;
        const pointerFocus = this.pointerFocusFrom(event, this.backButton);
        this.showPanel(previous.panel);
        this.focusControl(previous.trigger, pointerFocus);
        return;
      }
      const trigger = event.target.closest('[data-nav-target]');
      if (trigger) {
        const panel = this.panels.find(item => item.id === trigger.dataset.navTarget);
        if (!panel) return;
        const pointerFocus = this.pointerFocusFrom(event, trigger);
        this.stack.push({ panel: this.activePanel, trigger });
        this.showPanel(panel);
        this.focusControl(this.backButton, pointerFocus);
        return;
      }
      // Links keep their native behavior and the modal stays usable while a
      // destination loads. Canceled clicks, downloads, other tabs and external
      // protocol handlers must not dismiss the current menu or focus its opener.
      // This theme uses document navigation, not a client-side page router.
      const link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.hasAttribute('download')) return;
      const target = (link.getAttribute('target') || document.querySelector('base[target]')?.getAttribute('target') || '_self').toLowerCase();
      if (target !== '_self') return;
      let destination;
      try { destination = new URL(link.href, document.baseURI); } catch { return; }
      if (!['http:', 'https:'].includes(destination.protocol)) return;
      const current = new URL(window.location.href);
      if (destination.origin === current.origin && destination.pathname === current.pathname && destination.search === current.search && (destination.hash || link.getAttribute('href').startsWith('#'))) return;
      const pending = { link, pointerFocus: this.pointerFocusFrom(event, link) };
      this.pendingNavigation = pending;
      // A later listener may cancel the click. This only clears bookkeeping;
      // it never closes the drawer, changes focus or performs navigation.
      queueMicrotask(() => {
        if (event.defaultPrevented && this.pendingNavigation === pending) this.pendingNavigation = null;
      });
    }

    filterDesigners() {
      const query = (this.search?.value || '').trim().toLocaleLowerCase();
      let visible = 0;
      this.rows.forEach(row => {
        row.hidden = !row.dataset.designerName.toLocaleLowerCase().includes(query);
        if (!row.hidden) visible++;
      });
      if (this.clearButton) this.clearButton.hidden = !this.search.value;
      const empty = this.querySelector('[data-designer-empty]');
      if (empty) empty.hidden = visible > 0;
      this.querySelector('[data-nav-designers] [data-nav-scroll]')?.scrollTo(0, 0);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        return;
      }
      if (event.key === 'Enter' && event.target === this.search) {
        // Lets mobile shoppers dismiss the keyboard without submitting a form.
        event.preventDefault();
        this.search.blur();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.drawer.querySelectorAll('a[href], button, input, [tabindex="0"]')]
        .filter(item => !item.disabled && !item.closest('[hidden], [inert]') && item.getClientRects().length);
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first?.focus();
      }
    }

    setupDesktop(signal) {
      const item = this.closest('.shopify-section')?.querySelector('.dn-designer-desktop');
      if (!item) return;
      const trigger = item.querySelector(':scope > .js-nav-link');
      const dropdown = item.querySelector('.js-dropdown');
      if (!trigger || !dropdown) return;
      let clickExpanded = false;
      const sync = () => {
        const visible = dropdown.classList.contains('is-visible');
        if (!visible) clickExpanded = false;
        trigger.setAttribute('aria-expanded', String(visible));
        dropdown.inert = !visible;
      };
      const setOpen = (open) => {
        clearTimeout(this.desktopCloseTimer);
        if (open) item.closest('.js-nav')?.querySelectorAll('.js-dropdown.is-visible').forEach(other => {
          if (other !== dropdown) other.classList.remove('is-visible');
        });
        dropdown.classList.toggle('is-visible', open);
        item.closest('#header')?.classList.toggle('is-megamenu-open', !!item.closest('.js-nav')?.querySelector('.js-dropdown.is-visible'));
        sync();
      };
      this.desktopObserver?.disconnect();
      this.desktopObserver = new MutationObserver(sync);
      this.desktopObserver.observe(dropdown, { attributes: true, attributeFilter: ['class'] });
      sync();
      trigger.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        // Pointer entry may already have opened it before this click.
        const open = !clickExpanded;
        setOpen(open);
        clickExpanded = open;
      }, { signal });
      trigger.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown' || event.key === ' ') {
          event.preventDefault(); setOpen(true); dropdown.querySelector('a')?.focus();
        }
      }, { signal });
      item.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'touch') setOpen(true);
      }, { signal });
      item.addEventListener('pointerleave', event => {
        if (event.pointerType === 'touch' || item.contains(event.relatedTarget)) return;
        this.desktopCloseTimer = setTimeout(() => {
          if (!item.matches(':hover') && !item.contains(document.activeElement)) setOpen(false);
        }, 200);
      }, { signal });
      item.addEventListener('focusout', event => {
        if (!item.contains(event.relatedTarget) && !item.matches(':hover')) setOpen(false);
      }, { signal });
      item.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          event.preventDefault(); event.stopPropagation(); setOpen(false); trigger.focus();
        }
      }, { signal });
      document.addEventListener('click', event => {
        if (!item.contains(event.target)) setOpen(false);
      }, { signal });
      this.breakpoint.addEventListener('change', () => setOpen(false), { signal });
    }
  }
  if (!customElements.get('header-drawer')) customElements.define('header-drawer', HeaderDrawer);
})();
